import React, { useEffect, useState } from "react";
import { Loader2, Plus } from "lucide-react";
import { toast } from "sonner";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { errMsg, isNoChange, SERVICE_CATALOG } from "./clientMeta";

const NAVY = { background: "#0B2545", color: "#FFFFFF" };

const Shell = ({ open, onClose, title, description, children, onSubmit, saving, submitLabel = "Save", testid, hideSubmit = false }) => (
  <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
    <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto" data-testid={testid}>
      <DialogHeader>
        <DialogTitle className="font-display" style={{ color: "#0B2545" }}>{title}</DialogTitle>
        {description ? <DialogDescription>{description}</DialogDescription> : null}
      </DialogHeader>
      <div className="grid gap-4 py-1">{children}</div>
      <DialogFooter>
        <Button variant="outline" onClick={onClose} data-testid={`${testid}-cancel`}>{hideSubmit ? "Close" : "Cancel"}</Button>
        {hideSubmit ? null : (
          <Button onClick={onSubmit} disabled={saving} style={NAVY} data-testid={`${testid}-save`}>
            {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}{submitLabel}
          </Button>
        )}
      </DialogFooter>
    </DialogContent>
  </Dialog>
);

const useSubmit = (fn, onDone, successMsg) => {
  const [saving, setSaving] = useState(false);
  const run = async (...args) => {
    setSaving(true);
    try {
      const res = await fn(...args);
      if (successMsg) toast.success(successMsg);
      onDone?.(res);
    } catch (e) {
      if (isNoChange(e)) { toast.info("No changes to save."); onDone?.(null); }
      else toast.error(errMsg(e, "Failed to save."));
    } finally { setSaving(false); }
  };
  return [saving, run];
};

const Field = ({ f, value, onChange }) => {
  const id = `cf-${f.key}`;
  let control;
  if (f.type === "textarea") {
    control = <Textarea id={id} rows={2} value={value ?? ""} placeholder={f.placeholder} onChange={(e) => onChange(e.target.value)} data-testid={id} />;
  } else if (f.type === "select") {
    control = (
      <Select value={value || undefined} onValueChange={onChange}>
        <SelectTrigger id={id} data-testid={id}><SelectValue placeholder="Select…" /></SelectTrigger>
        <SelectContent>{f.options.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}</SelectContent>
      </Select>
    );
  } else {
    control = <Input id={id} type={f.type || "text"} value={value ?? ""} placeholder={f.placeholder}
      step={f.type === "number" ? "0.01" : undefined} min={f.type === "number" ? "0" : undefined}
      onChange={(e) => onChange(e.target.value)} data-testid={id} />;
  }
  return (
    <div className={f.type === "textarea" ? "sm:col-span-2" : ""}>
      <Label htmlFor={id} className="text-xs font-semibold" style={{ color: "#0B2545" }}>
        {f.label}{f.required ? " *" : ""}
      </Label>
      <div className="mt-1.5">{control}</div>
    </div>
  );
};

export const FormDialog = ({ open, onClose, title, description, fields, initial, onSave, onSaved, testid = "client-form-dialog", submitLabel }) => {
  const [form, setForm] = useState({});
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { if (open) setForm(initial || {}); }, [open]);
  const [saving, run] = useSubmit(onSave, (res) => { onSaved?.(res); onClose(); }, "Saved.");
  const submit = () => {
    const missing = fields.find((f) => f.required && !String(form[f.key] ?? "").trim());
    if (missing) { toast.error(`${missing.label} is required.`); return; }
    const clean = Object.fromEntries(Object.entries(form).map(([k, v]) => [k, typeof v === "string" ? v.trim() : v]));
    run(clean);
  };
  return (
    <Shell open={open} onClose={onClose} title={title} description={description} onSubmit={submit}
           saving={saving} testid={testid} submitLabel={submitLabel}>
      <div className="grid gap-4 sm:grid-cols-2">
        {fields.map((f) => <Field key={f.key} f={f} value={form[f.key]} onChange={(v) => setForm((s) => ({ ...s, [f.key]: v }))} />)}
      </div>
    </Shell>
  );
};

export const ServicesDialog = ({ open, onClose, current, onSave, onSaved }) => {
  const [picked, setPicked] = useState([]);
  const [custom, setCustom] = useState("");
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { if (open) { setPicked(current || []); setCustom(""); } }, [open]);
  const options = Array.from(new Set([...SERVICE_CATALOG, ...(current || []), ...picked]));
  const toggle = (s) => setPicked((p) => (p.includes(s) ? p.filter((x) => x !== s) : [...p, s]));
  const addCustom = () => { const v = custom.trim(); if (v && !picked.includes(v)) setPicked((p) => [...p, v]); setCustom(""); };
  const [saving, run] = useSubmit(onSave, (res) => { onSaved?.(res); onClose(); }, "Services updated.");
  return (
    <Shell open={open} onClose={onClose} title="Edit services" description="Tick every service this client uses."
           onSubmit={() => run(picked)} saving={saving} testid="client-services-dialog">
      <div className="grid gap-2.5 sm:grid-cols-2">
        {options.map((s) => (
          <label key={s} className="flex items-center gap-2.5 rounded-lg border px-3 py-2 text-sm cursor-pointer hover:bg-slate-50"
                 style={{ borderColor: "#E2E8F0", color: "#0B2545" }}>
            <Checkbox checked={picked.includes(s)} onCheckedChange={() => toggle(s)} data-testid={`service-opt-${s.replace(/\W+/g, "-").toLowerCase()}`} />
            {s}
          </label>
        ))}
      </div>
      <div className="flex gap-2">
        <Input placeholder="Other service…" value={custom} onChange={(e) => setCustom(e.target.value)}
               onKeyDown={(e) => e.key === "Enter" && addCustom()} data-testid="service-custom-input" />
        <Button variant="outline" onClick={addCustom} data-testid="service-custom-add"><Plus className="h-4 w-4" /></Button>
      </div>
    </Shell>
  );
};

export const NoteDialog = ({ open, onClose, onSave, onSaved }) => {
  const [text, setText] = useState("");
  useEffect(() => { if (open) setText(""); }, [open]);
  const [saving, run] = useSubmit(onSave, (res) => { onSaved?.(res); onClose(); }, "Note added.");
  return (
    <Shell open={open} onClose={onClose} title="Add note" submitLabel="Add note" saving={saving} testid="client-note-dialog"
           onSubmit={() => (text.trim() ? run(text.trim()) : toast.error("Write a note first."))}>
      <Textarea rows={5} value={text} onChange={(e) => setText(e.target.value)} placeholder="What happened?" data-testid="client-note-input" />
    </Shell>
  );
};

export const EmailDialog = ({ open, onClose, to, onSave, onSaved }) => {
  const [subject, setSubject] = useState("");
  const [body, setBody] = useState("");
  useEffect(() => { if (open) { setSubject(""); setBody(""); } }, [open]);
  const [saving, run] = useSubmit(onSave, (res) => { onSaved?.(res); onClose(); }, "Email sent.");
  const submit = () => (subject.trim() && body.trim() ? run({ subject: subject.trim(), body: body.trim() }) : toast.error("Subject and message are required."));
  return (
    <Shell open={open} onClose={onClose} title="Send email" description={`To ${to}. Sends from your connected Gmail, or the T&J mailer if Gmail isn't connected.`}
           submitLabel="Send email" saving={saving} onSubmit={submit} testid="client-email-dialog">
      <div><Label className="text-xs font-semibold">Subject *</Label>
        <Input className="mt-1.5" value={subject} onChange={(e) => setSubject(e.target.value)} data-testid="client-email-subject" /></div>
      <div><Label className="text-xs font-semibold">Message *</Label>
        <Textarea className="mt-1.5" rows={7} value={body} onChange={(e) => setBody(e.target.value)} data-testid="client-email-body" /></div>
    </Shell>
  );
};

export const AppointmentDialog = ({ open, onClose, services, defaultLocation, canConfirm, onSave, onSaved }) => {
  const [f, setF] = useState({});
  useEffect(() => {
    if (open) setF({ date: "", time: "09:00", service: services?.[0] || "", location: defaultLocation || "", notes: "", confirm: false });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);
  const set = (k, v) => setF((s) => ({ ...s, [k]: v }));
  const [saving, run] = useSubmit(onSave, (res) => { onSaved?.(res); onClose(); }, "Appointment created.");
  const submit = () => (f.date && f.time && f.service.trim() ? run(f) : toast.error("Date, time and service are required."));
  const opts = Array.from(new Set([...(services || []), ...SERVICE_CATALOG]));
  return (
    <Shell open={open} onClose={onClose} title="Create appointment" description="Shows up on the Calendar and in Dispatch."
           submitLabel="Create appointment" saving={saving} onSubmit={submit} testid="client-appointment-dialog">
      <div className="grid gap-4 sm:grid-cols-2">
        <div><Label className="text-xs font-semibold">Date *</Label><Input className="mt-1.5" type="date" value={f.date || ""} onChange={(e) => set("date", e.target.value)} data-testid="appt-date" /></div>
        <div><Label className="text-xs font-semibold">Time *</Label><Input className="mt-1.5" type="time" value={f.time || ""} onChange={(e) => set("time", e.target.value)} data-testid="appt-time" /></div>
      </div>
      <div><Label className="text-xs font-semibold">Service *</Label>
        <Select value={f.service || undefined} onValueChange={(v) => set("service", v)}>
          <SelectTrigger className="mt-1.5" data-testid="appt-service"><SelectValue placeholder="Select a service" /></SelectTrigger>
          <SelectContent>{opts.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
        </Select></div>
      <div><Label className="text-xs font-semibold">Location</Label><Input className="mt-1.5" value={f.location || ""} onChange={(e) => set("location", e.target.value)} data-testid="appt-location" /></div>
      <div><Label className="text-xs font-semibold">Notes</Label><Textarea className="mt-1.5" rows={2} value={f.notes || ""} onChange={(e) => set("notes", e.target.value)} data-testid="appt-notes" /></div>
      {canConfirm ? (
        <label className="flex items-center gap-2 text-sm" style={{ color: "#0B2545" }}>
          <Checkbox checked={!!f.confirm} onCheckedChange={(v) => set("confirm", !!v)} data-testid="appt-send-confirmation" />
          Email a confirmation to the client
        </label>
      ) : null}
    </Shell>
  );
};

export const BulkStatusDialog = ({ open, onClose, count, options, onSave, onSaved }) => {
  const [value, setValue] = useState("");
  useEffect(() => { if (open) setValue(""); }, [open]);
  const [saving, run] = useSubmit(onSave, (res) => { onSaved?.(res); onClose(); });
  return (
    <Shell open={open} onClose={onClose} title={`Change status · ${count} selected`} submitLabel="Apply" saving={saving}
           testid="client-bulk-status-dialog" hideSubmit={!options} onSubmit={() => (value ? run(value) : toast.error("Pick a status."))}
           description={options ? "The new status is saved on every selected client." : undefined}>
      {options ? (
        <Select value={value || undefined} onValueChange={setValue}>
          <SelectTrigger data-testid="bulk-status-select"><SelectValue placeholder="Select a status" /></SelectTrigger>
          <SelectContent>{options.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}</SelectContent>
        </Select>
      ) : (
        <p className="text-sm" style={{ color: "#475569" }} data-testid="bulk-status-mixed">
          Your selection mixes client types, which use different statuses. Select clients of one type to change them together.
        </p>
      )}
    </Shell>
  );
};
